import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Mail, ArrowRight } from "lucide-react";

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/subscribe", { email: email.trim(), source: "catalog" });
      toast.success(data.message || "You're in.");
      setDone(true);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : detail || "Please enter a valid email.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="newsletter-section"
      className="relative overflow-hidden border-t border-border/60 grain bg-[hsl(var(--primary))]"
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[hsl(var(--accent))] font-medium mb-5">
              <Mail className="w-3.5 h-3.5" /> Inner circle
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[hsl(var(--primary-foreground))]">
              Be first to{" "}
              <em className="italic font-medium text-[hsl(var(--accent))]">new drops</em>.
            </h2>
            <p className="mt-5 text-base lg:text-lg text-[hsl(var(--primary-foreground))]/75 max-w-lg leading-relaxed">
              Early access to new lengths, colors, and limited pieces — plus
              subscriber-only pricing a day before everyone else.
            </p>
          </div>

          <div className="fade-up">
            {done ? (
              <div
                data-testid="newsletter-success"
                className="bg-[hsl(var(--background))] rounded-sm p-8 lg:p-10"
              >
                <div className="font-serif text-3xl text-foreground mb-2">
                  Welcome in.
                </div>
                <p className="text-muted-foreground">
                  Check your inbox for our first note — new drops land there
                  before anywhere else.
                </p>
              </div>
            ) : (
              <form
                data-testid="newsletter-form"
                onSubmit={submit}
                className="bg-[hsl(var(--background))] rounded-sm p-6 lg:p-8"
              >
                <label
                  htmlFor="newsletter-email"
                  className="text-xs uppercase tracking-[0.2em] text-foreground/70 font-semibold"
                >
                  Your email
                </label>
                <div className="mt-3 flex flex-col sm:flex-row gap-3">
                  <Input
                    id="newsletter-email"
                    data-testid="newsletter-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-12 rounded-sm bg-secondary/40 border-border/70 text-base"
                  />
                  <Button
                    data-testid="newsletter-submit"
                    type="submit"
                    disabled={loading}
                    className="h-12 px-6 rounded-sm bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] shrink-0"
                  >
                    {loading ? "Joining…" : "Join"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  One email per drop. Unsubscribe any time.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
